<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, computed, watch } from 'vue'

import Button from '~/ui/Button.vue'
import Input from '~/ui/Input.vue'
import Spacer from '~/ui/Spacer.vue'

const { pages } = defineProps<{ pages: NonnegativeInteger<number> }>()

const { t } = useI18n()

type NonnegativeInteger<T extends number>
  = `${T}` extends `-${any}` | `${any}.${any}` ? never : T

const page = defineModel<number>('page', {
  required: true,
  validator: (value: number) => value > 0
})

const goTo = ref<number | string>('' as const)

const range = (start: number, end: number) => Array.from({ length: end - start + 1 }, (_, i) => i + start)

const pageFromInput = (input: string | number): number =>
  input === 'NaN'
    ? pageFromInput('')
    : typeof input === 'string'
      ? pageFromInput(Number.parseInt(input))
      : Number.isNaN(input)
        ? 1
        : Math.min(Math.max(1, input), pages)

/* Render only pages nearby if >5 pages:
- If near the end, show 1, ..., end-4, end-3, end-2, end-1, end
- If smaller than -4 and larger than 4, show 1, ..., current-1, current, current+1, ..., end
- If near beginning, show 1, 2, 3, 4, 5, ..., 12
*/
const renderPages = computed(() => {
  const start = range(2, 5)
  const end = range(pages - 4, pages - 1)

  const pagesArray = [1]

  if (page.value < 5) pagesArray.push(...start)
  if (page.value >= 5 && page.value <= pages - 4) {
    pagesArray.push(page.value - 1)
    pagesArray.push(page.value)
    pagesArray.push(page.value + 1)
  }
  if (page.value > pages - 4) pagesArray.push(...end)
  pagesArray.push(pages)

  return pagesArray.filter((page, index, pages) => pages.indexOf(page) === index)
})

const setPage = () => {
  if (goTo.value === '') return
  page.value = pageFromInput(goTo.value)
}

watch(goTo, (potentiallyWrongValue) => {
  goTo.value = typeof potentiallyWrongValue === 'string'
    ? ''
    : pageFromInput(potentiallyWrongValue)
})

/* When user changes page, the "GoTo" input should be emptied */
watch(page, (_) => {
  goTo.value = ''
})
</script>

<!-- REDESIGN, without custom CSS -->

<template>
  <nav
    :aria-label="t('vui.aria.pagination.nav')"
    class="funkwhale pagination layout-row"
    role="navigation"
    ui-nowrap
  >
    <ul class="pages layout-row">
      <li class="visually-hidden-when-tiny">
        <Button
          class="color-default-ghost shape-field-minimal"
          ui-block-size="40"
          :disabled="page <= 1"
          :aria-label="t('vui.aria.pagination.gotoPrevious')"
          @click="page -= 1"
        >
          <Icon icon="bi:chevron-left" />
          <span class="visually-hidden-when-small"> {{ t('vui.pagination.previous') }}</span>
        </Button>
      </li>

      <Spacer
        li
        no-size
        grow
        class="visually-hidden-when-tiny"
      />

      <template
        v-for="(i, index) in (renderPages)"
        :key="i + page * 100000"
      >
        <li v-if="i <= pages && i > 0 && pages > 1">
          <Button
            class="color-default-ghost shape-circle"
            :aria-label="page !== i ? t('vui.aria.pagination.goToPage', i) : t('vui.aria.pagination.currentPage', page)"
            :aria-current="page === i ? 'step' : 'false'"
            ui-block-size="24"
            @click="page = i"
          >
            <!-- Vertically center numbers because Lato(latin) makes them look too far up (lining figures visual fix) -->
            <span style="margin-top: 2px;">
              {{ i }}
            </span>
          </Button>
        </li>
        <li
          v-if="i + 1 < renderPages[index + 1]!"
          style="user-select: none;"
          class="visually-hidden-when-tiny"
        >
          {{ t('vui.pagination.ellipsis') }}
        </li>
      </template>

      <Spacer
        li
        no-size
        grow
        class="visually-hidden-when-tiny"
      />

      <li class="visually-hidden-when-tiny">
        <Button
          class="color-default-ghost shape-field-minimal"
          ui-block-size="40"
          :disabled="page >= pages"
          :aria-label="t('vui.aria.pagination.gotoNext')"
          @click="page += 1"
        >
          <span class="visually-hidden-when-small">{{ t('vui.pagination.next') }}</span>
          <Icon icon="bi:chevron-right" />
        </Button>
      </li>
    </ul>
    <!-- \d{1,100} -->
    <Spacer size-8 />
    <Input
      v-model.number="goTo"
      ui-inline-size="128"
      ui-block-size="40"
      :placeholder="t('vui.pagination.enterPageNumber')"
      inputmode="numeric"
      pattern="[0-9]*"
      :aria-label="t('vui.aria.pagination.goToPage', { goTo })"
      @click.stop
      @keyup.enter="setPage"
      @blur="setPage"
    />
  </nav>
</template>

<style>
.funkwhale.pagination {
    li {
        padding-left: 0;
    }

    container-type: inline-size;
    container-name: pagination;

    justify-content: space-between;

    > ul.pages {
      width: 100%;
      list-style: none;
      margin: 0;
      padding: 0;
      gap: 4px;

      > li {
        margin: 0;
        text-align: center;

        &::before {
            display: none;
        }

        &:not(:first-child):not(:last-child):not(.visually-hidden-when-tiny) {
          min-width: 34px;
        }

        &:first-child > .funkwhale.button,
        &:last-child > .funkwhale.button {
          width: 94px;
          text-align: center;
        }
      }
    }
    /* People using a screen reader want to be able to use default 'previous page', 'next page' controls*/
    @container pagination (max-width: 600px) {
      .visually-hidden-when-small:not(:has(:focus)):not(:has(:active)) {
        border: 0;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        white-space: nowrap;
        width: 1px;
      }
    }

    /* 2. Hide the 'tiny' items when container drops below 400px */
    @container pagination (max-width: 450px) {
    > ul.pages {
        gap: 0px;
    }

      .visually-hidden-when-tiny:not(:has(:focus)):not(:has(:active)) {
        border: 0;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        white-space: nowrap;
        width: 1px;
      }
      >[ui-inline-size] {
        flex-shrink: 1;
        flex-grow: 0;
        min-width: 48px;
      }
    }
  }
</style>
